import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { environment } from '@deltager/organizers/admin/environments/environment';

import { Operation } from 'fast-json-patch';
import { Event } from '@deltager/organizers/admin/modules/events/create-event/model/event.model';
import { Attribute } from '@deltager/organizers/admin/modules/events/create-event/model/attribute.model';
import { GeneralAttribute } from '@deltager/organizers/admin/modules/events/create-event/model/generalAttribute.model';
import { AttributeType } from '@deltager/organizers/admin/modules/events/create-event/model/attribute-type.enum';
import { BuiltinAttribute } from '@deltager/organizers/admin/modules/events/create-event/model/builtin-attributes.model';
import { Template } from '@deltager/organizers/admin/modules/events/create-event/model/template.model';
import { AuthService } from '../auth/auth.service';
import { EventSessionBatchUpdate } from '@deltager/organizers/admin/shared/models/EventSessionBatchUpdate';
import { TranslateService } from '@ngx-translate/core';
import { EventHeader } from '@deltager/organizers/admin/modules/events/create-event/model/event-header';
import { OrganizerConsentSettings } from '@deltager/organizers/admin/modules/events/create-event/model/organizer-consent-settings.model';
import { Wizard } from '../../modules/events/create-event/model/wizard.model';
import { map, tap } from 'rxjs/operators';
import { CustomValidation } from '@deltager/organizers/admin/core/models/custom-validation.model';
import { CachedNetworkRequestService } from '@deltager/organizers/admin/shared/services/cached-network-request.service';
import { InstructorActivityLeadersList } from '@deltager/organizers/admin/modules/organizers/participants-list/participants-list/participants-instructors-list/models/instructor-activity-leaders-list.model';
import { PageInfo } from '@deltager/organizers/admin/shared/models/Paging';
import { Option } from '../../shared/models/option.model';
import { CancelEventRequest } from '@deltager/organizers/admin/modules/cancellation/components/event-cancellation/cancel-event-request.model';
import { EventCancellationInfo } from '@deltager/organizers/admin/modules/cancellation/components/event-cancellation/event-cancellation-info.model';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cachedNetworkRequest: CachedNetworkRequestService,
    private injector: Injector,
  ) {}

  private get translationService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  private organizerTemplates: Template[];
  private organizerConsentSettings: OrganizerConsentSettings;

  getCustomFieldValidations(organizerId: number): Observable<CustomValidation[]> {
    return this.cachedNetworkRequest.get<CustomValidation[]>(
      `${environment.apiBaseURL}/organizers/${organizerId}/custom_validations`,
    );
  }

  getEvents(): Observable<any> {
    return this.http.get(`${environment.apiBaseURL}/events`, {
      observe: 'response',
    });
  }

  updateEvent(event: Event): Observable<Event> {
    return this.http.put(`${environment.apiBaseURL}/events/${event.id}`, event).pipe((data: any) => data.Data);
  }

  createEvent(event: Event, wizardId: number): Observable<Event> {
    return this.http.post<Event>(
      `${environment.apiBaseURL}/events/?wid=${wizardId}`,
      event,
      this.gethHttpOptions(false),
    );
  }

  saveEvent(event: Event, eventOperations: Operation[]): Observable<Event> {
    return this.http.patch<Event>(
      `${environment.apiBaseURL}/events/${event.id}`,
      eventOperations,
      this.gethHttpOptions(false, true),
    );
  }

  removeEvent(eventId: number): Observable<any> {
    return this.http.delete<any>(`${environment.apiBaseURL}/events/${eventId}`);
  }

  archiveEvent(eventId: number): Observable<any> {
    return this.http.put<any>(`${environment.apiBaseURL}/events/${eventId}/archive`, {});
  }

  cancelEventWithRefund(eventId: number, requestDto: CancelEventRequest): Observable<boolean> {
    return this.http.put<boolean>(`${environment.apiBaseURL}/events/${eventId}/cancel-with-refund`, requestDto);
  }

  cancelEvent(eventId: number, requestDto: CancelEventRequest): Observable<boolean> {
    return this.http.put<boolean>(`${environment.apiBaseURL}/events/${eventId}/cancel`, requestDto);
  }

  refundOrders(eventId: number): Observable<boolean> {
    return this.http.put<boolean>(`${environment.apiBaseURL}/events/${eventId}/refund`, {});
  }

  unarchiveEvent(eventId: number): Observable<any> {
    return this.http.put<any>(`${environment.apiBaseURL}/events/${eventId}/unarchive`, {});
  }

  getTemplateList(organizerId: number, useCache?: boolean): Observable<Template[]> {
    if (useCache && this.organizerTemplates && this.organizerTemplates.length) {
      return of(this.organizerTemplates);
    }

    return this.http
      .get<
        Template[]
      >(`${environment.apiBaseURL}/organizers/${organizerId}/registrationpagetemplates`, this.gethHttpOptions(false))
      .pipe(tap(templates => (this.organizerTemplates = templates)));
  }

  getWizardStep1(wizardId: number): Observable<Wizard> {
    if (wizardId === 0) {
      return of(null);
    }

    return this.http.get<Wizard>(
      `${environment.apiBaseURL}/events/wizards/${wizardId}/step1`,
      this.gethHttpOptions(false),
    );
  }

  getOrganizerEventConsents(organizerId: number): Observable<OrganizerConsentSettings> {
    if (this.organizerConsentSettings) {
      return of(this.organizerConsentSettings);
    }

    return this.http
      .get<OrganizerConsentSettings>(
        `${environment.apiBaseURL}/organizers/${organizerId}/event-consents`,
        this.gethHttpOptions(false),
      )
      .pipe(tap(s => (this.organizerConsentSettings = s)));
  }

  public verifyEventUrlAvailable(eventId: number, url: string): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiBaseURL}/events/${eventId}/urls/${url}`, {
      ...this.getHttpOptions(),
    });
  }

  getCurrentAttributes(attr: GeneralAttribute[], checkRemovedOptions = false) {
    const updatedAttr = this.separateToDefaultAttributes(attr, AttributeType.customAttribute);
    return updatedAttr.map(attribute => Attribute.transformToAttribute(attribute, checkRemovedOptions));
  }

  getCurrentBuiltinAttributes(attr: GeneralAttribute[]) {
    const updatedAttr = this.separateToDefaultAttributes(attr, AttributeType.builtinAttribute);
    return updatedAttr.map(attribute => BuiltinAttribute.transformToBuiltinAttribute(attribute));
  }

  getParticipantEventAttribute(eventId: number, attributeId: number): Observable<Attribute> {
    return this.http.get<Attribute>(
      `${environment.participantApiBaseURL}/events/${eventId}/attributes/${attributeId}`,
      this.gethHttpOptions(false),
    );
  }

  separateToDefaultAttributes(attr: GeneralAttribute[], attributeType: AttributeType) {
    let attribute: GeneralAttribute[];
    let builtinAttribute: GeneralAttribute[];

    if (attributeType === AttributeType.builtinAttribute) {
      builtinAttribute = attr.filter(item => {
        if (item.typeOfAttr === AttributeType.builtinAttribute) {
          return item;
        }
      });
      return builtinAttribute;
    } else {
      attribute = attr.filter(item => {
        if (item.typeOfAttr === AttributeType.customAttribute) {
          return item;
        }
      });
      return attribute;
    }
  }

  getEventHeader(eventId: number, refreshCache: boolean = false): Observable<EventHeader> {
    return this.cachedNetworkRequest.get<EventHeader>(
      `${environment.apiBaseURL}/events/${eventId}/header?refreshCache=${refreshCache}`,
    );
  }

  removeEventTemporaryIdentifiers(formValue) {
    const resultFormValue = formValue;
    delete resultFormValue.isShowBankAccount;
    delete resultFormValue.isShowImage;
    return resultFormValue;
  }

  publishEvent(eventId: number): Observable<Event> {
    return this.http.post<Event>(
      `${environment.apiBaseURL}/events/published/${eventId}`,
      {},
      this.gethHttpOptions(false),
    );
  }

  getEventInstructorsActivities(eventId: number, pageInfo: PageInfo): Observable<InstructorActivityLeadersList> {
    let params = new HttpParams();
    Object.keys(pageInfo).map(key => (params = params.set(key, pageInfo[key])));

    return this.http.get<InstructorActivityLeadersList>(
      `${environment.apiBaseURL}/events/${eventId}/instructors-activities`,
      { params },
    );
  }

  getOpenActiveList(): Observable<string> {
    return this.http.get<string>(`https://openactive.io/activity-list/activity-list.jsonld`);
  }

  gethHttpOptions(setContentType: boolean = true, isPatch: boolean = false): object {
    const result = {
      headers: new HttpHeaders(),
    };

    if (setContentType) {
      result.headers.append('Content-Type', 'application/json');
    }

    if (isPatch) {
      result.headers = result.headers.append('Content-Type', 'application/json-patch+json');
    }

    const token = this.authService.getAuthToken();
    if (token !== null && token.length !== 0) {
      result.headers = result.headers.set('Authorization', 'Bearer ' + token);
    }

    return result;
  }

  getEventSessionsById(eventId: number): Observable<any> {
    return this.http.get(`${environment.apiBaseURL}/eventsessions/${eventId}/sessions`).pipe((data: any) => data.Data);
  }

  batchUpdateEventSessions(eventId: number, data: EventSessionBatchUpdate): Promise<number> {
    return this.http
      .put<number>(
        `${environment.apiBaseURL}/eventsessions/${eventId}/sessions/batchUpdate`,
        data,
        this.getHttpOptions(),
      )
      .toPromise();
  }

  setEventParticipantListMode(eventId: number, mode: number) {
    return this.http
      .get(`${environment.oldApiBaseURL}/event/${eventId}/reportmode/${mode}`, this.getHttpOptions())
      .pipe(map(response => response as any));
  }

  public getDefaultAttributeName(): string {
    return this.translationService.instant('eventsetup.form_builder.default_attribute_name');
  }

  getDefaultAttributeOptionName(): string {
    return this.translationService.instant('eventsetup.form_builder.default_attribute_option_name');
  }

  public getTypeOptions(organizerId: number): Observable<Option[]> {
    return this.http.get<Option[]>(`${environment.apiBaseURL}/organizers/${organizerId}/types/options`);
  }

  public getLevelOptions(organizerId: number): Observable<Option[]> {
    return this.http.get<Option[]>(`${environment.apiBaseURL}/organizers/${organizerId}/levels/options`);
  }

  public getCancellationInfo(eventId: number): Observable<EventCancellationInfo> {
    return this.http.get<EventCancellationInfo>(`${environment.apiBaseURL}/events/${eventId}/cancellation-info`);
  }

  public getSurveyTemplateOptions(): Observable<Option[]> {
    return this.http.get<Option[]>(`${environment.apiBaseURL}/survey-templates/options`);
  }

  private getHttpOptions(): object {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.authService.getAuthToken(),
      }),
    };
  }
}
